export const CORE_STEPS = [
  "basics",
  "experience",
  "education",
  "skills",
  "summary",
  "languages",
  "finalization",
] as const;

export const ADDITIONAL_STEPS = [
  "projects",
  "certifications",
  "volunteer",
  "awards",
  "interests",
  "publications",
  "references",
] as const;

export const STEPS = CORE_STEPS;

export type CoreBuilderStep = (typeof CORE_STEPS)[number];
export type AdditionalBuilderStep = (typeof ADDITIONAL_STEPS)[number];
export type CustomBuilderStep = `custom.${string}`;
export type BuilderStep = CoreBuilderStep | AdditionalBuilderStep | CustomBuilderStep;
export type BuilderView = BuilderStep | "templates";

export const STEP_LABELS: Record<string, string> = {
  basics: "Coordonnées",
  experience: "Expérience",
  education: "Formation",
  skills: "Compétences",
  summary: "Profil professionnel",
  languages: "Langues",
  projects: "Projets",
  certifications: "Certifications",
  volunteer: "Bénévolat",
  awards: "Récompenses",
  interests: "Centres d’intérêt",
  publications: "Publications",
  references: "Références",
  finalization: "Finalisation",
};

/**
 * Étiquette affichable d'une étape/vue du builder. Retombe sur la clé brute
 * si aucune étiquette n'est enregistrée (ex: sections `custom.xxx`), pour
 * ne jamais afficher `undefined`.
 */
export const getStepLabel = (step: string): string => STEP_LABELS[step] ?? step;

export const ADDITIONAL_SECTION_MARKER = "\u2063";

type ResumeSections = Partial<
  Record<AdditionalBuilderStep, { name?: string; items?: unknown[] }>
> & {
  custom?: Record<string, { name?: string; items?: unknown[] } | undefined>;
};

export const isCustomBuilderStep = (step: string): step is CustomBuilderStep =>
  step.startsWith("custom.");

export const isAdditionalBuilderStep = (step: string): step is AdditionalBuilderStep =>
  ADDITIONAL_STEPS.includes(step as AdditionalBuilderStep);

export const getAddedSteps = (
  layout: ReadonlyArray<ReadonlyArray<ReadonlyArray<string>>>,
  sections: ResumeSections,
): Array<AdditionalBuilderStep | CustomBuilderStep> => {
  const seen = new Set<string>();
  return layout
    .flat(2)
    .filter(
      (step): step is AdditionalBuilderStep | CustomBuilderStep =>
        isAdditionalBuilderStep(step) || isCustomBuilderStep(step),
    )
    .filter((step) => {
      if (seen.has(step)) return false;
      seen.add(step);
      return true;
    })
    .filter((step) => {
      if (isCustomBuilderStep(step)) return Boolean(sections.custom?.[step.slice(7)]);
      const section = sections[step];
      return Boolean(
        section &&
          ((section.items?.length ?? 0) > 0 || section.name?.endsWith(ADDITIONAL_SECTION_MARKER)),
      );
    });
};

export const getBuilderSteps = (
  layout: ReadonlyArray<ReadonlyArray<ReadonlyArray<string>>>,
  sections: ResumeSections,
): BuilderStep[] => [
  ...CORE_STEPS.filter((step) => step !== "finalization"),
  ...getAddedSteps(layout, sections),
  "finalization",
];
